import { InviteMemberButton } from "@components/common/invite-member-button";
import { createMemberOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useTeamsAccess } from "@hooks/access-control/use-teams-access";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
import { useCompanyId } from "@context-providers/company-id-provider";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { setOne } from "@store/members/members-slice";
import { convertToMemberTypes } from "@store/members/members-slice-utils";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedTeamMembersSelector } from "@store/teams/teams-selector";
import { addMembersToTeam } from "@store/teams/teams-slice-thunk";
import { nounPluralize } from "@utils/data-display";
import { delay } from "@utils/time-utils";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";
import { Dialog } from "@flat-ui";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { EMAILS_ONLY_PLACEHOLDER, MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import { MAX_INVITED_MEMBERS } from "@components/teams/teams-constants";
/** Renders the invite member button and the functionality in team page */
export function InviteMemberToTeam({
  team,
  buttonVariant = "outlined",
  shouldUseAutoWidth = false
}) {
  const companyMembers = useCompanyMembers();
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    respondToTeamInvite
  } = useRespondToInvite();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    trackEvent
  } = useTrackEvent();
  const teamMembers = useAppSelector(selectedTeamMembersSelector);
  const {
    canInviteMembersToTeam
  } = useTeamsAccess();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const teamMembersIdSet = useMemo(() => new Set(teamMembers.map(member => member.userResponse.identity)), [teamMembers]);
  const sortedCompanyMembers = sortMembersByNameOrEmail(companyMembers);

  /** Reformation company members data for faro autocomplete component */
  const autocompleteOptions = useMemo(() => sortedCompanyMembers.map(member_0 => {
    const isAlreadyMemberInTeam = teamMembersIdSet.has(member_0.identity);
    return createMemberOption({
      member: member_0,
      isDisabled: isAlreadyMemberInTeam,
      disabledMessage: isAlreadyMemberInTeam ? `Already in this ${TEAM_DISPLAY_NAME}` : ""
    });
  }), [sortedCompanyMembers, teamMembersIdSet]);
  const {
    selectedMembers,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete({
    entityName: TEAM_DISPLAY_NAME,
    existingMembers: teamMembers.map(member_1 => ({
      identity: member_1.userResponse.identity,
      email: member_1.userResponse.email
    })),
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map(member_2 => member_2.identity),
    maxInvitedMembers: MAX_INVITED_MEMBERS,
    invalidInputMessage: "Enter a valid email address"
  });

  // Disable the `Send Invite` button if no user has been selected or the selection/input is not valid.
  const shouldSendDisable = shouldDisableSendInvite(selectedMembers, isMemberInputValid, MAX_INVITED_MEMBERS);

  // If the user doesn't have permission to invite members to the team, don't render anything
  if (!canInviteMembersToTeam) {
    return null;
  }

  /** Default setup when invite dialog open */
  function onOpenInviteDialog() {
    reset();
    setIsDialogOpen(true);
  }

  /** Close the dialog and reset loading state */
  async function closeDialog() {
    setIsDialogOpen(false);
    // Delay to ensure dialog closes before resetting loading state
    // This prevents duplicate invitation requests
    await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
    setIsLoading(false);
  }

  /** Submit members to be invited */
  async function handleConfirm() {
    setIsLoading(true);
    trackEvent({
      name: TeamEvents.inviteMember,
      props: {
        numberOfMembers: uniqueSelectedValues.length
      }
    });
    try {
      // Add members
      const response = await dispatch(addMembersToTeam({
        companyId,
        teamId: team.id,
        members: uniqueSelectedValues
      })).unwrap();
      const newMemberResponse = response.data.filter(allMembers => !companyMembers.some(oldMembers => oldMembers.identity === allMembers.userResponse.identity));
      if (newMemberResponse && newMemberResponse.length > 0) {
        newMemberResponse.forEach(member_3 => {
          const newMember = convertToMemberTypes(member_3.userResponse);

          /**
           * The new member should be added to the member store as well.
           * We should use the setOne because we are updating the company role for all members
           * that are present in this list for a Project Role and we should have a Company Role.
           * If we update the role to project in the members store, we will not be able to remove
           * these members from the member page. The user needs to reload the page to have the company
           * role again.
           */
          dispatch(setOne(newMember));
        });
      }
      respondToTeamInvite(response, uniqueSelectedValues.length);
      void closeDialog();
    } catch (error) {
      setIsLoading(false);
      handleErrorWithToast({
        id: `inviteMemberToTeam-${Date.now().toString()}`,
        title: `Cannot invite the ${nounPluralize({
          shouldShowCounter: false,
          counter: uniqueSelectedValues.length,
          word: "member"
        })}`,
        error
      });
    }
  }
  return <>
      <InviteMemberButton buttonVariant={buttonVariant} onClick={onOpenInviteDialog} shouldUseAutoWidth={shouldUseAutoWidth} />

      <Dialog title={`Invite members to ${TEAM_DISPLAY_NAME}`} confirmText="Send Invite" open={isDialogOpen} onConfirm={handleConfirm} showSpinner={isLoading} isConfirmDisabled={shouldSendDisable} onCancel={closeDialog}>
        <MembersAutocomplete isRequired label="Members" hasAutoFocus selectedOptions={selectedMembers} options={autocompleteOptions} isValid={isNewEmailValid} helperText={helperText} handleMemberSelect={handleMemberSelect} onInputChange={onInputChange} placeHolder={EMAILS_ONLY_PLACEHOLDER} />
      </Dialog>
    </>;
}